import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Text,
  Input,
  Spinner,
  useColorModeValue,
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  Divider,
  InputRightElement,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import InfiniteScroll from "react-infinite-scroll-component";
import { setSelectedAdAccounts } from "store/BmAccounts/bmAccountSlice";
import { FiSearch, FiX } from "react-icons/fi";
import { useDispatch } from "react-redux";
import { setAccessAllAdAccounts } from "store/campaigns/campaignsSlice";

function SearchAdaccount({
  accountOptions,
  onAccountChange,
  onSearchInputChange,
  onSearchInputTouched,
  onInitialisation,
  AdsAccountIdFromParams,
  pageMeta = [],
  onLoadMore,
  isAdAccountLoading,
  isAdAccountFetching, // <-- used for list spinner during search
  context = "",
}) {
  const dispatch = useDispatch();
  const { pageSize, after, hasNextPage } = pageMeta;
  const [searchAdAccountValue, setSearchAdAccountValue] = useState("");
  const [selectedAccountValue, setSelectedAccountValue] = useState(null);
  const [filteredAccounts, setFilteredAccounts] = useState([]);
  const [allLoadedAccounts, setAllLoadedAccounts] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  const textColor = useColorModeValue("brand.900", "brand.500");
  const hoverButton = useColorModeValue("purple.200", "brand.800");
  const borderButton = useColorModeValue("brand.900", "brand.800");
  const popoverBg = useColorModeValue("white", "card.dark");
  const borderColor = useColorModeValue("brand.200", "brand.400");
  const inputBg = useColorModeValue("white", "transparent");
  const placeholderColor = useColorModeValue("gray.400", "gray.500");
  const searchResultColor = useColorModeValue("gray.700", "brand.100");
  const searchHoverResultColor = useColorModeValue("brand.900", "whiteAlpha.900");
  const SerachTast = useColorModeValue("gray.500", "gray.300");
  const bgBrand = useColorModeValue("brand.100", "brand.500");

  // Local search state helpers
  const isSearching = searchAdAccountValue.length > 0;

  // Filter accounts based on searchAdAccountValue
  const filteredAccountsData =
    accountOptions
      ?.filter(
        (account) =>
          account?.name?.toLowerCase().includes(searchAdAccountValue.toLowerCase()) ||
          account?.account_id?.includes(searchAdAccountValue)
      )
      .map((account) => ({
        value: account?.account_id,
        label: account?.name?.length > 50 ? account?.name?.slice(0, 50) + "..." : account?.name,
        account,
      })) || [];

  // Update the filtered accounts state when new data is available
  useEffect(() => {
    // Get the stored account from localStorage
    const storedAccount = JSON.parse(localStorage.getItem("selectedAdsAccount"));

    if (filteredAccountsData?.length > 0) {
      setFilteredAccounts((prev) => {
         // Create a set of existing account values (account_ids) in prev (previously filtered accounts)
        const existingAccountValues = new Set(prev?.map((account) => account?.value));
         // Filter out the new accounts that already exist in prev (based on value)
        const newAccounts = filteredAccountsData?.filter(
          (account) => !existingAccountValues.has(account?.value)
        );

        // If there are new accounts, add them
        const updatedAccounts = [...prev, ...newAccounts];

        if (storedAccount && !existingAccountValues.has(storedAccount?.value)) {
          const exists = updatedAccounts?.some((a) => a?.value === storedAccount?.value);
          if (!exists) updatedAccounts?.unshift(storedAccount);
        }
        return updatedAccounts;
      });
    }
  }, [accountOptions]); // eslint-disable-line

  useEffect(() => {
    const storedAccount = JSON.parse(localStorage.getItem("selectedAdsAccount"));
    if (accountOptions && accountOptions.length > 0) {
      setAllLoadedAccounts((prev) => {
        const existingAccountIds = new Set(prev.map((a) => a?.account_id));
        const newAccounts = accountOptions.filter((a) => !existingAccountIds.has(a?.account_id));
        const updated = [...prev, ...newAccounts];

        if (storedAccount && !existingAccountIds.has(storedAccount?.account?.account_id)) {
          const exists = updated.some((a) => a?.account_id === storedAccount?.account?.account_id);
          if (!exists) updated.unshift(storedAccount?.account);
        }
        return updated;
      });
    }
  }, [accountOptions]);

  useEffect(() => {
    if (accountOptions && accountOptions?.length > 0) {
      setAllLoadedAccounts((prev) => {
        const existingAccountIds = new Set(prev.map((a) => a?.account_id));
        const newAccounts = accountOptions?.filter((a) => !existingAccountIds.has(a?.account_id));
        return [...prev, ...newAccounts];
      });
    }
  }, [accountOptions]);

  useEffect(() => {
    if (allLoadedAccounts?.length > 0) {
      dispatch(setSelectedAdAccounts(allLoadedAccounts));
    }
  }, [allLoadedAccounts, AdsAccountIdFromParams, dispatch]);

  useEffect(() => {
    if (filteredAccounts && filteredAccounts?.length > 0) {
      if (selectedAccountValue?.value === "123") {
        dispatch(setAccessAllAdAccounts(true));
        return;
      }

      if (!selectedAccountValue) {
        const storedAccount = JSON.parse(localStorage.getItem("selectedAdsAccount"));
        const accountFromParams = AdsAccountIdFromParams;

        const accountFromParamsExists = filteredAccounts?.some(
          (acc) => acc?.value === accountFromParams
        );

        if (accountFromParamsExists) {
          const account = filteredAccounts.find((acc) => acc?.value === accountFromParams);
          setSelectedAccountValue(account);
          onInitialisation(account?.value);
          return;
        }

        if (accountFromParams) {
          onSearchInputChange(accountFromParams);
          onSearchInputTouched?.(true);
          return;
        }

        const initialAccountId = storedAccount?.value;
        if (initialAccountId) {
          const account = filteredAccounts?.find((acc) => acc?.value === initialAccountId);
          if (account) {
            setSelectedAccountValue(account);
            onInitialisation(account?.value);
          }
        } else {
          const defaultAccount = filteredAccounts[0];
          setSelectedAccountValue(defaultAccount);
          onInitialisation(defaultAccount?.value);
        }
      }
    } else {
      setSelectedAccountValue(null);
      onInitialisation(null);
    }
  }, [
    filteredAccountsData,
    AdsAccountIdFromParams,
    onInitialisation,
    searchAdAccountValue,
    filteredAccounts,
    onSearchInputChange,
    onSearchInputTouched,
    selectedAccountValue,
    dispatch,
  ]);

  const handleSelectChange = (account) => {
    if (account) localStorage.setItem("selectedAdsAccount", JSON.stringify(account));
    setSelectedAccountValue(account);
    onAccountChange?.(account);

    setFilteredAccounts((prev) => {
      if (!prev.some((item) => item?.value === account?.value)) return [...prev, account];
      return prev;
    });

    setSearchAdAccountValue("");
    dispatch(setAccessAllAdAccounts(false));
    setIsOpen(false);
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchAdAccountValue?.length > 2) {
        onSearchInputChange?.(searchAdAccountValue);
        onSearchInputTouched?.(true);
      } else if (searchAdAccountValue?.length === 0) {
        onSearchInputChange?.("");
      }
    }, 400);
    return () => clearTimeout(delayDebounceFn);
  }, [searchAdAccountValue, onSearchInputChange, onSearchInputTouched]);

  const handleScrollToBottom = useCallback(() => {
    if (hasNextPage && !isAdAccountFetching) onLoadMore?.(after);
  }, [hasNextPage, after, isAdAccountFetching, onLoadMore]);

  const handleAccessAllClick = () => {
    dispatch(setAccessAllAdAccounts(true));
    const accessAll = { label: "Access all Ad accounts", value: "123" };
    setSelectedAccountValue(accessAll);
    localStorage.setItem("selectedAdsAccount", JSON.stringify(accessAll));
    onAccountChange?.(accessAll);
    setSearchAdAccountValue("");
    setIsOpen(false);
  };

  const accountsToShow = isSearching
    ? filteredAccountsData.filter((a) => a?.value !== "123")
    : filteredAccounts.filter((a) => a?.value !== "123");

  return (
    <Box width="280px" position="relative">
      <Popover trigger="click" placement="bottom-start" isOpen={isOpen} onClose={() => setIsOpen(false)}>
        <PopoverTrigger>
          <Button
            variant="unstyled"
            width="280px"
            padding="10px"
            fontSize="13px"
            textAlign="left"
            size="sm"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            fontWeight={"normal"}
            onClick={() => setIsOpen(true)}
            _hover={{ bg: hoverButton }}
            borderWidth={1}
            color={textColor}
            borderColor={isOpen ? "brand.900" : borderButton}
          >
            {isAdAccountLoading ? (
              <>
                Loading... <Spinner size="sm" ml={2} />
              </>
            ) : (
              <>
                <span>
                  {selectedAccountValue
                    ? selectedAccountValue.label.length > 36
                      ? selectedAccountValue.label.slice(0, 36) + "..."
                      : selectedAccountValue.label
                    : "Select an Ad Account"}
                </span>
                <ChevronDownIcon boxSize={5} />
              </>
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent
          width="400px"
          maxHeight="500px"
          border="1px solid"
          borderColor={borderColor}
          borderRadius="md"
          boxShadow="md"
        >
          <PopoverArrow bg={popoverBg} />
          <PopoverBody>
            {/* Search */}
            <Box>
              <InputGroup>
                <InputLeftElement pointerEvents="none" mt={-1}>
                  <FiSearch size={18} color={useColorModeValue("#666", "#AAA")} />
                </InputLeftElement>
                <Input
                  borderRadius="full"
                  value={searchAdAccountValue}
                  onChange={(e) => setSearchAdAccountValue(e.target.value)}
                  placeholder="Search for an ad account"
                  size="sm"
                  bg={inputBg}
                  color={searchResultColor}
                  _placeholder={{ color: placeholderColor }}
                  border="1px solid"
                  borderColor={borderColor}
                  _focus={{ borderColor: "brand.400", boxShadow: "0 0 0 1px brand.400" }}
                />
                {searchAdAccountValue && (
                  <InputRightElement mt={-1}>
                    <FiX size={18} cursor="pointer" onClick={() => setSearchAdAccountValue("")} color={SerachTast} />
                  </InputRightElement>
                )}
              </InputGroup>
            </Box>

            {selectedAccountValue?.value !== "123" && context === "campaign" && (
              <Button
                width="100%"
                onClick={handleAccessAllClick}
                mt="10px"
                p={2}
                size="sm"
                borderRadius="full"
                variant="solid"
                colorScheme="brand"
                _hover={{ color: "white" }}
              >
                Access all Ad accounts
              </Button>
            )}

            <Divider my={3} borderColor={borderColor} />

            {/* LIST AREA */}
            <Box overflowY="auto" id="scrollable-menu" maxHeight="350px">
              {/* Inline spinner while searching */}
              {isSearching && isAdAccountFetching && (
                <Box display="flex" alignItems="center" justifyContent="center" py={2} gap={2}>
                  <Spinner size="sm" />
                  <Text fontSize="sm" color={placeholderColor}>Searching…</Text>
                </Box>
              )}

              {/* Empty state when search completed and no results */}
              {isSearching && !isAdAccountFetching && accountsToShow.length === 0 && (
                <Box textAlign="center" py={2}>
                  <Text fontSize="sm" color={placeholderColor}>No ad accounts found.</Text>
                </Box>
              )}

              {/* Results list (disable endless scroll while searching) */}
              {accountsToShow.length > 0 && (
                <InfiniteScroll
                  dataLength={accountsToShow.length}
                  next={handleScrollToBottom}
                  hasMore={!isSearching && hasNextPage}
                  loader={
                    !isSearching ? (
                      <Box display="flex" justifyContent="center" padding="10px">
                        <Spinner size="sm" />
                      </Box>
                    ) : null
                  }
                  scrollThreshold={0.9}
                  scrollableTarget="scrollable-menu"
                >
                  <Box>
                    {accountsToShow.map((account) => (
                      <Box
                        key={account.value}
                        padding="10px"
                        cursor="pointer"
                        borderRadius="md"
                        fontSize="12px"
                        color={searchResultColor}
                        bg={selectedAccountValue?.value === account.value ? bgBrand : "transparent"}
                        _hover={{ bg: bgBrand, color: searchHoverResultColor }}
                        onClick={() => handleSelectChange(account)}
                      >
                        {account.label}
                        <Box as="span" display="block" fontSize="10px">
                          {account.value}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </InfiniteScroll>
              )}
            </Box>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
}

export default SearchAdaccount;
